import { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { X, Play } from "lucide-react";

const items = [
  { type: "image", src: "/media/pew-1.jpeg", caption: "Engraved biscuit die — close detail" },
  { type: "image", src: "/media/pew-2.jpeg", caption: "Moulding roller cavity layout" },
  { type: "video", src: "/media/pew-v1.mp4", poster: "/media/pew-1.jpeg", caption: "Engraving in process" },
  { type: "image", src: "/media/pew-3.jpeg", caption: "Workshop floor, Patiala" },
  { type: "video", src: "/media/pew-v2.mp4", poster: "/media/pew-2.jpeg", caption: "Roller machining" },
  { type: "image", src: "/media/pew-4.jpeg", caption: "Finished tooling, ready to ship" },
  { type: "video", src: "/media/pew-v3.mp4", poster: "/media/pew-3.jpeg", caption: "Run test before dispatch" },
];

export default function GalleryPage() {
  const [open, setOpen] = useState(null);

  return (
    <div data-testid="gallery-page" className="bg-[var(--bg-primary)]">
      <Navbar />

      <section className="pt-36 pb-16 bg-[var(--maroon-950)] text-white relative grain">
        <div className="container-edge relative">
          <div className="overline text-[#D38888]">Gallery</div>
          <h1 className="font-display text-5xl md:text-7xl mt-5 leading-[1] tracking-tight">
            From the floor.
          </h1>
          <p className="mt-6 max-w-2xl text-[#E8DCC8]/85 text-lg">
            Photographs and short clips from our workshop in Patiala — the engraving, the rollers, and the small details that make a die run for years.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-edge">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-[300px] md:auto-rows-[360px] gap-4">
            {items.map((it, i) => {
              const span = i % 5 === 0 ? "lg:row-span-2 lg:col-span-2" : "";
              return (
                <button
                  key={i}
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setOpen(i)}
                  className={`group relative overflow-hidden border border-[var(--border-subtle)] hover:border-[var(--maroon-700)] transition-colors ${span}`}
                >
                  {it.type === "image" ? (
                    <img
                      src={it.src}
                      alt={it.caption}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <>
                      <video
                        className="w-full h-full object-cover"
                        src={it.src}
                        muted
                        loop
                        playsInline
                        autoPlay
                        poster={it.poster}
                      />
                      <div className="absolute inset-0 bg-black/15 grid place-items-center">
                        <div className="w-14 h-14 rounded-full bg-white/95 grid place-items-center text-[var(--maroon-900)] group-hover:scale-110 transition">
                          <Play size={22} />
                        </div>
                      </div>
                    </>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <div className="font-mono-accent text-[11px] tracking-[0.2em] uppercase text-white/80">
                      {String(i + 1).padStart(2, "0")} · {it.type}
                    </div>
                    <div className="text-white text-sm mt-1">{it.caption}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {open !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-4"
          onClick={() => setOpen(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 text-white p-2"
            onClick={() => setOpen(null)}
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {items[open].type === "image" ? (
              <img src={items[open].src} alt={items[open].caption} className="w-full max-h-[85vh] object-contain" />
            ) : (
              <video src={items[open].src} controls autoPlay className="w-full max-h-[85vh]" />
            )}
            <div className="text-white/80 text-center mt-4 font-mono-accent text-xs tracking-[0.2em] uppercase">
              {items[open].caption}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
